import {
    HandHoldingMoneyIcon,
    ImplementationIcon,
    LeafIcon,
    MeasureTypeIcon,
    SizingIcon,
    UnitCostIcon,
} from "components/atoms/Icon";
import { Header, Paragraph } from "components/atoms/Typography";
import { ReactNode } from "react";
import { displayValueWithUnit, getDisplayInfo } from "utils/formatting";

export const buildHeaderTitleAndUnitsStruct = (data: MeasureReportHeader) => {
    const { formatFunction: formatSize } = getDisplayInfo("carbonReductionMeasureSize");

    const { formatFunction: formatUnitCost, unit: costUnit } = getDisplayInfo("carbonReductionMeasureUnitCost");

    const options: ProjectReportHeaderAttributeProps[] = [
        {
            title: "Type",
            titleValue: data.carbonReductionMeasureCategory,
            icon: <MeasureTypeIcon />,
        },
        {
            title: "Implementation",
            titleValue: `${data.yearApplied} (${data.carbonReductionMeasureLife} yrs)`,
            icon: <ImplementationIcon />,
        },
    ];

    if (data.carbonReductionMeasureSize !== undefined && data.carbonReductionMeasureSizeUnit)
        options.push({
            title: "Sizing",
            titleValue: formatSize(data.carbonReductionMeasureSize),
            icon: <SizingIcon />,
            unit: ` ${getCarbonReductionMeasureSizeUnitHumanReadableString(
                data.carbonReductionMeasureSizeUnit,
                data.carbonReductionMeasureSize > 1 ? "plural" : "singular"
            )}`,
        });

    if (data.carbonReductionMeasureUnitCost !== undefined && data.carbonReductionMeasureSizeUnit)
        options.push({
            title: "Unit cost",
            titleValue: displayValueWithUnit(data.carbonReductionMeasureUnitCost, costUnit, (v) => formatUnitCost(v)),
            icon: <UnitCostIcon />,
            unit: `/${getCarbonReductionMeasureSizeUnitHumanReadableString(
                data.carbonReductionMeasureSizeUnit,
                "singular"
            )}`,
        });

    return options;
};

const getCarbonReductionMeasureSizeUnitHumanReadableString = (
    unit: string,
    singularOrPlural: "singular" | "plural"
) => {
    if (unit === "none" && singularOrPlural === "singular") return "unit";
    if (unit === "none" && singularOrPlural === "plural") return "units";

    const humanReadable = measureSizeUnitToHumanReadableSingular[unit];

    if (singularOrPlural === "singular") return humanReadable || unit;
    return `${measureSizeUnitSingularPluralMap[unit] || humanReadable || unit}`;
};

interface ProjectReportHeaderAttributeProps {
    title: string;
    titleValue: string;
    icon: ReactNode;
    unit?: string;
}

export function ProjectReportHeaderAttribute({ title, titleValue, icon, unit }: ProjectReportHeaderAttributeProps) {
    return (
        <div className="project-report-header__measure_attribute">
            {icon}
            <Header size="small">{title}:</Header>
            <Paragraph size="regular">{`${titleValue}${unit || ""}`}</Paragraph>
        </div>
    );
}

export function ImpactCardLoading() {
    return (
        <div className="small-cards">
            {Array.from({ length: 5 }).map((_, i) => (
                <div
                    className="card small--card"
                    data-testid="card-loading"
                    // eslint-disable-next-line react/no-array-index-key
                    key={i}
                >
                    <div
                        className="loading"
                        style={{
                            height: "12px",
                            width: "30%",
                            borderRadius: "12px",
                        }}
                    />
                    <div
                        className="loading"
                        style={{
                            height: "12px",
                            width: "80%",
                            borderRadius: "12px",
                        }}
                    />
                </div>
            ))}
        </div>
    );
}

export function LargeCardLoading() {
    const widths = [86, 150, 50, 172, 50, 150, 50, 150, 50];

    return (
        <div className="large-card large-card--loading" data-testid="card-loading">
            {widths.map((width, index) => (
                <div
                    className="large-card--loading__grid-item"
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${width}$${index}`}
                >
                    <div
                        className="loading"
                        style={{
                            height: "12px",
                            width: `${width}px`,
                            borderRadius: "12px",
                        }}
                    />
                </div>
            ))}
        </div>
    );
}

export const buildTotalCarbonEmissionSavingsCardStruct = (
    carbonEmissionsData: MeasureReportTotalCarbonEmissionsCard,
    energyData: MeasureReportTotalEnergyConsumptionCard
): CardSection<string>[] => [
    {
        title: {
            key: "totalEnergyConsumptionSavings",
            icon: <LeafIcon />,
            value: energyData.totalEnergyConsumptionSavings,
        },
        body: [
            {
                key: "totalEnergyConsumptionSavingsElectricity",
                value: energyData.totalEnergyConsumptionSavingsElectricity,
            },
            {
                key: "totalEnergyConsumptionSavingsNaturalGas",
                value: energyData.totalEnergyConsumptionSavingsNaturalGas,
            },
        ],
    },
    {
        title: {
            key: "totalCarbonEmissionSavings",
            icon: <LeafIcon />,
            value: carbonEmissionsData.totalCarbonEmissionSavings,
        },
        body: [
            {
                key: "totalCarbonEmissionSavingsElectricity",
                value: carbonEmissionsData.totalCarbonEmissionSavingsElectricity,
            },
            {
                key: "totalCarbonEmissionSavingsNaturalGas",
                value: carbonEmissionsData.totalCarbonEmissionSavingsNaturalGas,
            },
        ],
    },
];

export interface CardSection<T extends keyof any> {
    title: {
        key: T;
        humanReadableOverride?: string;
        icon: React.ReactNode;
        value: number;
    };
    body: {
        key: T;
        humanReadableOverride?: string;
        value: number;
        percentage?: number;
    }[];
}

export const buildTotalCostAndSavingsCardDataStruct = (
    data: ProjectReportTotalCostAndSavingsCardDatum | ProjectReportTotalCostAndSavingsCardTotal
): CardSection<string>[] => {
    const struct: CardSection<string>[] = [
        {
            title: {
                key: "totalEnergyCostSavings",
                icon: <LeafIcon />,
                value: data.totalEnergyCostSavings,
            },
            body: [
                {
                    key: "totalUtilityCostSavingsElectricity",
                    value: data.totalUtilityCostSavingsElectricity,
                },
                {
                    key: "totalUtilityCostSavingsNaturalGas",
                    value: data.totalUtilityCostSavingsNaturalGas,
                },
                {
                    key: "totalCarbonPolicyCostSavings",
                    value: data.totalCarbonPolicyCostSavings,
                },
            ],
        },
        {
            title: {
                key: "totalMeasureCost",
                icon: <HandHoldingMoneyIcon />,
                value: data.totalMeasureCost,
            },
            body: [
                {
                    key: "incrementalCost",
                    value: data.incrementalCost,
                },
            ],
        },
    ];

    if (data.likeForLikeCost !== undefined) {
        struct[1].body.unshift({
            key: "likeForLikeCost",
            value: data.likeForLikeCost,
        });
    }
    return struct;
};

export const buildCashFlowTableColumnKeys = (selected: "total" | "incremental") => {
    const keys = [
        "calendarYear",
        "likeForLikeCost",
        "incrementalCost",
        "utilityCostSavingsNaturalGas",
        "utilityCostSavingsElectricity",
        "carbonPolicyCostSavings",
        "cashFlowAnnual",
        "revenue",
        "totalIncentives",
    ];
    if (selected === "incremental") {
        keys.splice(1, 1);
    }
    return keys;
};

export const buildCashFlowChartKeys = (selected: "total" | "incremental") => {
    const keys = [
        "likeForLikeCost",
        "incrementalCost",
        "utilityCostSavingsNaturalGas",
        "utilityCostSavingsElectricity",
        "carbonPolicyCostSavings",
        "revenue",
        "totalIncentives",
    ];
    if (selected === "incremental") {
        keys.splice(0, 1);
    }
    return keys;
};

export const measureSizeUnitToHumanReadableSingular: {
    [unit: string]: string;
} = {
    cfm_of_ahu_supply_air_rate: "cfm",
    heating_load_cop: "ton",
    kw: "kW",
    kwh: "kWh",
    m2: "m²",
    m2_of_gfa: "m² of GFA",
    m2_of_roof_area: "m² of roof",
    m2_of_window_area: "m² of window",
    seal: "seal",
    ton: "ton",
};

export const measureSizeUnitSingularPluralMap: {
    [unit: string]: string;
} = {
    dryer: "dryers",
    elevator: "elevators",
    escalator: "escalators",
    seal: "seals",
    suite: "suites",
    ton: "tons",
    zone: "zones",
    heating_load_cop: "tons",
};
